import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { apiService } from '../../services/api';
import { formatDateTimeSaudi, formatTimeSaudi } from '../../utils/saudiTime';

export default function SalesRevenue() {
  const { user } = useAuth();
  const [data, setData] = useState({ orders: [], totalRevenue: 0, summary: { successfulCount: 0, failedCount: 0 } });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rangeStart, setRangeStart] = useState(''); // YYYY-MM-DD
  const [rangeEnd, setRangeEnd] = useState(''); // YYYY-MM-DD
  const role = user && String(user.role).toLowerCase();
  const isSalesUser = role === 'custom_role_sales';
  const isAdmin = role === 'admin' || role === 'master_admin';

  const computedParams = useMemo(() => {
    if (!isAdmin) return null;
    if (!rangeStart || !rangeEnd) return null;
    return { startDate: rangeStart, endDate: rangeEnd };
  }, [isAdmin, rangeStart, rangeEnd]);

  const load = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const params = isAdmin ? (computedParams || undefined) : undefined;
      const res = await apiService.orders.getSalesOrdersRevenue(params);
      setData({
        orders: res.data?.orders || [],
        totalRevenue: res.data?.totalRevenue ?? 0,
        summary: res.data?.summary || { successfulCount: 0, failedCount: 0 },
        compare: undefined,
      });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load revenue');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, computedParams]);

  useEffect(() => {
    load();
  }, [load]);

  const applyRange = useCallback(() => {
    if (!rangeStart || !rangeEnd) return;
    if (rangeStart > rangeEnd) {
      setError('Start date must be before end date.');
      return;
    }
    load();
  }, [rangeStart, rangeEnd, load]);

  const clearRange = useCallback(() => {
    setRangeStart('');
    setRangeEnd('');
  }, []);

  const formatDateTime = (d) => (isSalesUser ? formatTimeSaudi(d) : formatDateTimeSaudi(d));

  const averageBucket = useMemo(() => {
    const success = Number(data.summary?.successfulCount || 0);
    const total = Number(data.totalRevenue || 0);
    if (!success || success <= 0) return 0;
    return total / success;
  }, [data.summary?.successfulCount, data.totalRevenue]);

  return (
    <div className="p-3 sm:p-4 md:p-6">
      <div className="bg-white rounded-lg shadow-md border-l-4 border-green-500 p-3 sm:p-4 md:p-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-700 mb-2 sm:mb-4">Revenue</h1>
        <p className="text-gray-600 text-sm mb-2">
          {isSalesUser
            ? 'Revenue from orders where payment has been received — today only.'
            : isAdmin
            ? 'Revenue from sales orders where payment has been received. View all or filter by date.'
            : 'Revenue from sales orders where payment has been received.'}
        </p>
        {isAdmin && (
          <div className="mb-4 grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <div className="text-sm font-medium text-gray-700 mb-2">Date range</div>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="date"
                  value={rangeStart}
                  onChange={(e) => setRangeStart(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-gray-700"
                />
                <span className="text-gray-500">to</span>
                <input
                  type="date"
                  value={rangeEnd}
                  onChange={(e) => setRangeEnd(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-gray-700"
                />
              </div>
              <div className="mt-2 flex items-center gap-3">
                <button
                  type="button"
                  onClick={applyRange}
                  disabled={!rangeStart || !rangeEnd || loading}
                  className="px-3 py-2 rounded-lg bg-[#735dff] text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Apply
                </button>
                <button
                  type="button"
                  onClick={clearRange}
                  disabled={loading}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  Clear (All time)
                </button>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}
        {loading && <div className="py-8 text-center text-gray-500">Loading…</div>}
        {!loading && (
          <>
            <div className="mb-4 grid grid-cols-2 lg:grid-cols-3 gap-3">
              <div className="bg-white border rounded-lg p-3">
                <div className="text-xs text-gray-500">Successful orders (Paid)</div>
                <div className="text-lg sm:text-xl font-bold text-gray-800 tabular-nums">
                  {Number(data.summary?.successfulCount || 0)}
                </div>
              </div>
              <div className="bg-white border rounded-lg p-3">
                <div className="text-xs text-gray-500">Failed orders</div>
                <div className="text-lg sm:text-xl font-bold text-gray-800 tabular-nums">
                  {Number(data.summary?.failedCount || 0)}
                </div>
              </div>
              <div className="bg-white border rounded-lg p-3">
                <div className="text-xs text-gray-500">Average bucket</div>
                <div className="text-lg sm:text-xl font-bold text-gray-800 tabular-nums">
                  SAR {Number(averageBucket).toFixed(2)}
                </div>
              </div>
            </div>
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="text-sm text-gray-600">
                Total revenue
              </div>
              <div className="text-xl sm:text-2xl font-bold text-green-800 tabular-nums">SAR {Number(data.totalRevenue).toFixed(2)}</div>
            </div>
            {data.orders.length === 0 ? (
              <p className="text-gray-500 py-4">No orders with payment received yet.</p>
            ) : (
              <div className="space-y-3">
                {data.orders.map((o) => (
                  <div
                    key={o._id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 py-3 px-3 sm:py-2 sm:px-3 bg-gray-50 rounded-lg border border-gray-100"
                  >
                    <div className="min-w-0">
                      <span className="font-medium text-gray-800">{o.orderNo}</span>
                      <span className="text-gray-500 text-sm ml-0 sm:ml-2 block sm:inline">{formatDateTime(o.dateTime)}</span>
                      {o.customerName && (
                        <span className="text-gray-500 text-sm ml-0 sm:ml-2 block sm:inline">· {o.customerName}</span>
                      )}
                    </div>
                    <span className="font-semibold text-gray-800 tabular-nums flex-shrink-0">SAR {Number(o.totalAmount || 0).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
